import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Link,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import DataTable from "react-data-table-component";
import { createColumns } from "../../helpers";
import { DATATABLE_CUSTOM_STYLES } from "../../constants/datatableStyles";
import CustomPagination from "../../Components/CustomPagination";
import PageTitle from "../../Components/UI/PageTitle";
import EyeDetailsIcon from "../../Components/Svgs/EyeDetailsIcon";
import RowLoader from "../../Components/UI/RowLoader";
import ErrorAnimationComponent from "../../assets/lottie/ErrorAnimation/ErrorAnimationComponent";
import { clearAllPackagesByCategoryData } from "../../Redux/AllPackagesByCategory/AllPackagesByCategorySlice";
import { FetchAllPackageManagementByCategory } from "../../Redux/AllPackagesByCategory/AllPackagesByCategoryAction";
import { toSnakeCase } from "../../_helper";

const ListPackageManagementDetails = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const filterBy = searchParams.get("filterBy");

  const [sortKey, setSortKey] = useState(
    new URLSearchParams(location.search).get("sortKey") || ""
  );
  const [sortDirection, setSortDirection] = useState(
    new URLSearchParams(location.search).get("sortDirection") || ""
  );
  const [currentPage, setCurrentPage] = useState(1);
  const { packageManagementId } = useParams();

  const dispatch = useDispatch();
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  const {
    isLoading,
    pagination,
    error,
    AllPackagesByCategoryData,
    packageCategoryName,
    partnerTypeName,
  } = useSelector((state) => state.AllPackagesByCategory);

  useEffect(() => {
    dispatch(
      FetchAllPackageManagementByCategory(
        packageManagementId,
        currentPage,
        sortKey,
        sortDirection,
        filterBy
      )
    );

    return () => {
      dispatch(clearAllPackagesByCategoryData());
    };
  }, [
    currentPage,
    sortKey,
    sortDirection,
    packageManagementId,
    dispatch,
    filterBy,
  ]);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 640);
    };
    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set("page", currentPage);
    if (sortKey) params.set("sortKey", sortKey);
    if (sortDirection) params.set("sortDirection", sortDirection);
  }, [currentPage, sortKey, sortDirection]);

  const handleSortChange = (column, direction) => {
    switch (column.sortKey) {
      case "PackageName":
        setSortKey("package_name_en");
        break;
      case "PackageCategoryName":
        setSortKey("category_name");
        break;
      case "DirectEm":
        setSortKey("pv_direct_value");
        break;
      default:
        setSortKey(toSnakeCase(column.sortKey));
    }

    setSortDirection(direction);
    setCurrentPage(1); // Reset to first page on sort change
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const filterAndSortData = () => {
    return AllPackagesByCategoryData;
  };

  const hiddenColumns = ["id"]; // Add column keys you want to hide

  const customColumns = [
    {
      name: "Details",
      selector: (row) => row.name + " - custom",
      ui: (row) => (
        <div className="tw-flex tw-gap-1">
          <Link to={`package-details/${row.id}`}>
            <EyeDetailsIcon width="30px" height="30px" />
          </Link>
        </div>
      ),
    },
  ];
  const columns = useMemo(() => {
    return createColumns(
      AllPackagesByCategoryData,
      customColumns,
      hiddenColumns,
      true
    );
  }, [AllPackagesByCategoryData]).map((col, index) => ({
    ...col,
    width:
      col.name === "Num" ? "70px" : col.name === "Category" ? "90px" : "150px",
    sortable: [
      "Num",
      "Package Name",
      "Package Category Name",
      "Registration Price",
      "Courses Price",
      "Total Price",
      "Is Active",
      "Num Of Partners",
      "Pv Active",
      "Pv Upliner Active",
      "Partner Type",
      "Direct Em",
      "Created At",
    ].includes(col.name),
    style: {
      ...(!isSmallScreen &&
        index === 0 && {
          position: "sticky",
          left: 0,
          zIndex: 2,
          backgroundColor: "#fff",
        }),
      ...(!isSmallScreen &&
        index === 1 && {
          position: "sticky",
          left: "70px",
          zIndex: 2,
          backgroundColor: "#fff",
        }),
      ...(!isSmallScreen &&
        index === 2 && {
          position: "sticky",
          left: "220px",
          zIndex: 2,
          backgroundColor: "#fff",
        }),
    },
  }));

  if (error) return <ErrorAnimationComponent message={error} />;

  const currentType = partnerTypeName || packageCategoryName;

  const title =
    packageManagementId === "all"
      ? "All Packages"
      : `List Of ${currentType || ""}`;
  return (
    <>
      {AllPackagesByCategoryData?.length > 0 && (
        <PageTitle
          title={title}
          BreadcrumbItems={[
            { title: "Home", link: "/" },
            { title: "Package Management", link: "/package-management" },
            { title: "Packages List" },
          ]}
        />
      )}

      <div className="bg-white flex flex-col mt-4">
        <div className="bg-white shadow-lg rounded-lg flex flex-col p-2 gap-2 mb-5">
          <div className="table-with-sticky-cols">
            <DataTable
              columns={columns}
              data={filterAndSortData()}
              customStyles={DATATABLE_CUSTOM_STYLES}
              progressPending={isLoading}
              onSort={handleSortChange}
              progressComponent={<RowLoader />}
              sortServer
            />
          </div>

          {!isLoading && (
            <div className="flex justify-center mt-4">
              <CustomPagination
                totalPages={pagination?.last_page}
                currentPage={pagination?.current_page || 1}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ListPackageManagementDetails;
